import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Switch as ChakraSwitch,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useContext, useEffect, useState } from 'react'
import { RiAddLine, RiDeleteBin6Line, RiPencilLine } from 'react-icons/ri'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'

import { Modal } from '../../components/Modal'
import { Pagination } from '../../components/Pagination'
import { SearchContext } from '../../context/SearchContext'
import { PaymentMethod } from '../../Interfaces/paymentMethod'
import { api } from '../../services/api'
import { usePaymentMethods } from '../../services/hooks/usePaymentMethods'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0)

export default function PaymentMethods() {
  const toast = useToast()
  const [page, setPage] = useState(1)

  const { queryContextParams, clearQuery } = useContext(SearchContext)

  const queryParams = queryContextParams ?? {}

  const { data, isLoading, isFetching, error, refetch } = usePaymentMethods({
    page,
    limit: 10,
    ...queryParams,
  })

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentMethod, setCurrentMethod] = useState<PaymentMethod | null>(null)

  useEffect(() => {
    refetch()
  }, [queryContextParams])

  useEffect(() => {
    return () => {
      clearQuery()
    }
  }, [])

  const removeMutation = useMutation(
    async (paymentMethod: PaymentMethod) => {
      const response = await api.delete(`payment-methods/${paymentMethod.id}`)
      toast({ description: 'Forma de pagamento removida com sucesso', status: 'success' })
      await refetch()
      onClose()
      return response.data
    },
    {
      onError: ({ response }) => {
        const errors = response?.data?.errors || []
        if (errors.length) {
          errors.forEach((err) => toast({ description: err.message, status: 'error' }))
        } else {
          toast({ description: 'Não foi possível remover a forma de pagamento', status: 'error' })
        }
      },
    },
  )

  const toggleStatusMutation = useMutation(async (paymentMethod: PaymentMethod) => {
    const response = await api.patch(`payment-methods/${paymentMethod.id}/toggle-status`)
    toast({
      description: `Forma de pagamento ${response.data?.data?.is_active ? 'ativada' : 'desativada'} com sucesso`,
      status: 'success',
    })
    await refetch()
    return response.data
  })

  const handleDelete = (paymentMethod: PaymentMethod) => {
    setCurrentMethod(paymentMethod)
    onOpen()
  }

  const confirmDelete = () => {
    if (currentMethod && !removeMutation.isLoading) {
      removeMutation.mutate(currentMethod)
    }
  }

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" p="8">
      <Flex mb="8" justifyContent="space-between" align="center">
        <Heading size="lg" fontWeight="normal">
          Formas de Pagamento
          {!isLoading && isFetching && <Spinner size="sm" color="gray.500" ml="4" />}
        </Heading>

        <Button
          as={Link}
          to="/payment-methods/create"
          size="sm"
          fontSize="sm"
          colorScheme="pink"
          leftIcon={<Icon as={RiAddLine} fontSize="20" />}>
          Nova Forma
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Flex justify="center">
          <Text>Falha ao obter as formas de pagamento</Text>
        </Flex>
      ) : (
        <Table colorScheme="whiteAlpha">
          <Thead>
            <Tr>
              <Th>Nome</Th>
              <Th>Código</Th>
              <Th isNumeric>Taxa %</Th>
              <Th isNumeric>Taxa Fixa</Th>
              <Th isNumeric>Dias Proc.</Th>
              <Th>Requer aprovação</Th>
              <Th>Status</Th>
              <Th textAlign="right">Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data?.paymentMethods.map((method) => (
              <Tr key={method.id}>
                <Td>
                  <Text fontWeight="bold">{method.name}</Text>
                  {method.description && (
                    <Text fontSize="sm" color="gray.300">
                      {method.description}
                    </Text>
                  )}
                </Td>
                <Td>
                  <Badge colorScheme="purple">{method.code}</Badge>
                </Td>
                <Td isNumeric>{Number(method.fee_percentage ?? 0).toFixed(2)}%</Td>
                <Td isNumeric>{formatCurrency(Number(method.fee_fixed ?? 0))}</Td>
                <Td isNumeric>{Number(method.processing_days ?? 0)}</Td>
                <Td>{method.requires_approval ? 'Sim' : 'Não'}</Td>
                <Td>
                  <HStack spacing={3}>
                    <ChakraSwitch
                      colorScheme="pink"
                      isChecked={method.is_active}
                      onChange={() => toggleStatusMutation.mutate(method)}
                      isDisabled={toggleStatusMutation.isLoading}
                    />
                    <Text>{method.is_active ? 'Ativo' : 'Inativo'}</Text>
                  </HStack>
                </Td>
                <Td textAlign="right">
                  <HStack spacing={2} justify="flex-end">
                    <IconButton
                      as={Link}
                      to={`/payment-methods/edit/${method.id}`}
                      aria-label="Editar"
                      icon={<RiPencilLine />}
                      size="sm"
                      colorScheme="purple"
                    />
                    <IconButton
                      aria-label="Remover"
                      icon={<RiDeleteBin6Line />}
                      size="sm"
                      colorScheme="red"
                      onClick={() => handleDelete(method)}
                      isLoading={removeMutation.isLoading && currentMethod?.id === method.id}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <Pagination totalCountOfRegisters={data?.totalCount} currentPage={page} onPageChange={setPage} />

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setCurrentMethod(null)
          onClose()
        }}
        title="Remover Forma de Pagamento"
        buttonTitle="Remover"
        buttonAction={confirmDelete}>
        <VStack spacing={3} align="stretch">
          <Text>
            Tem certeza que deseja remover a forma de pagamento <strong>{currentMethod?.name}</strong>?
          </Text>
          <Text fontSize="sm" color="gray.300">
            Esta ação não poderá ser desfeita.
          </Text>
        </VStack>
      </Modal>
    </Box>
  )
}
